import { put, call, all, select } from 'redux-saga/effects';
import axios from 'axios';
import { matchRoutes } from 'react-router-config';
import { ALL_FOLDER_NAME, SCRAPS_FOLDER_NAME } from '@wix/da-url';
import {
  getPageSize,
  getPagingMode,
} from '@wix/da-shared-react/pkg/publicSession/selectors';
import { getHomeSectionInitActions } from '../store/initActions/home';
import { getAboutSectionInitActions } from '../store/initActions/about';
import { getGallectionsSectionInitActions } from '../store/initActions/gallections';
import { getShopSectionInitActions } from '../store/initActions/shop';
import { getPostsSectionInitActions } from '../store/initActions/posts';
import { getStatsSectionInitActions } from '../store/initActions/stats';
import { getSubscriptionsSectionInitActions } from '../store/initActions/subscriptions';
import { fetchSection, fetchSectionSuccess } from '../actions/sections';
import { routes, getSectionKeys } from '../routes';
import { DEVIATIONS_PER_FETCH_COUNT } from '../streams/folderDeviations';
import { API_BASE_PATH } from '../../constants';
import { PapiInitGallectionRequestParams } from '../../types/papi';
import { PostsType } from '../../types/posts';
import { FULLTEXT_JOURNALS_PER_FETCH, POSTS_PER_FETCH } from '../streams/posts';

function fetch(section, params) {
  return axios
    .get(`${API_BASE_PATH}/init/${section}`, { params })
    .catch(response => false);
}

export default function* fetchSectionData(action) {
  const { location } = action;

  if (location.state && location.state.withoutSectionLoad) {
    return;
  }

  const routeData = matchRoutes(routes, location.pathname).pop();
  const sectionKey = routeData.route.key;
  const sectionKeys = getSectionKeys();

  if (!sectionKeys.includes(sectionKey)) {
    return;
  }

  yield put(fetchSection(sectionKey));

  const routeParams = routeData.match.params;

  let requestParams;

  switch (sectionKey) {
    case 'gallery':
    case 'favourites':
      const pageSize = yield select(getPageSize);
      const pagingMode = yield select(getPagingMode);
      const isPaginated = pagingMode === 'page';
      const gallectionRequestParams: PapiInitGallectionRequestParams = {
        username: routeParams.username,
        deviations_limit: isPaginated ? pageSize : DEVIATIONS_PER_FETCH_COUNT,
        with_subfolders: true,
      };

      if (routeParams.folderId !== undefined) {
        gallectionRequestParams.folder_id = routeParams.folderId;
      } else {
        switch (routeParams.folderName) {
          case SCRAPS_FOLDER_NAME:
            gallectionRequestParams.scraps_folder = true;
            break;
          case ALL_FOLDER_NAME:
            gallectionRequestParams.all_folder = true;
            break;
          default:
        }
      }

      requestParams = gallectionRequestParams;
      break;
    case 'shop':
      requestParams = {
        username: routeParams.username,
        shop_section: routeParams.shopSection,
      };
      break;
    case 'posts':
      requestParams = {
        username: routeParams.username,
        posts_section: routeParams.postsSection,
        limit: FULLTEXT_JOURNALS_PER_FETCH,
        order: routeParams.sortOrder,
      };
      if (routeParams.postsSection === PostsType.POLLS) {
        requestParams.limit = POSTS_PER_FETCH;
      } else {
        requestParams.expand = 'deviation.fulltext';
      }
      break;
    case 'subscriptions':
      requestParams = {
        username: routeParams.username,
        section: routeParams.tierContentType,
        tier_deviationid: routeParams.tierDeviationid,
      };
      break;
    default:
      requestParams = {
        username: routeParams.username,
      };
  }

  const result = yield call(fetch, sectionKey, requestParams);

  if (result && result.data && result.status === 200) {
    let sectionInitActions;
    switch (sectionKey) {
      case 'about':
        sectionInitActions = getAboutSectionInitActions(result.data);
        break;
      case 'home':
        sectionInitActions = getHomeSectionInitActions(result.data);
        break;
      case 'gallery':
      case 'favourites':
        sectionInitActions = getGallectionsSectionInitActions(
          sectionKey,
          result.data,
          requestParams
        );
        break;
      case 'posts':
        sectionInitActions = getPostsSectionInitActions(
          result.data,
          requestParams
        );
        break;
      case 'shop':
        sectionInitActions = getShopSectionInitActions(
          result.data,
          requestParams
        );
        break;
      case 'stats':
        sectionInitActions = getStatsSectionInitActions(
          result.data,
          requestParams
        );
        break;
      case 'subscriptions':
        sectionInitActions = getSubscriptionsSectionInitActions(
          result.data,
          requestParams
        );
        break;
      default:
        break;
    }
    yield all(
      sectionInitActions.filter(item => item).map(initAction => put(initAction))
    );
    yield put(fetchSectionSuccess(sectionKey));
  }
}
